<script setup>
import { capitalize, intersection, isEqual, snakeCase, uniqBy } from 'lodash-es';
import HawkHandsontable from '~/common/components/organisms/hawk-handsontable/hawk-handsontable.vue';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { useTagsStore } from '~/common/stores/tags.store';
import { convertTableUnitsData } from '~/common/utils/common.utils.js';
import { useDashboardScheduleStore } from '~/dashboard/store/dashboard-schedule.store.js';
import { useDashboardStore } from '~/dashboard/store/dashboard.store.js';

const props = defineProps({
  data: {
    type: Object,
  },
  id: {
    type: String,
  },
  // eslint-disable-next-line vue/prop-name-casing
  content_height: {
    type: Number,
  },
});

const { common_store } = useCommonImports();
const tags_store = useTagsStore();
const $services = inject('$services');
const dashboard_schedule_store = useDashboardScheduleStore();
const dashboard_store = useDashboardStore();

const force_update = ref(0);
const activities = ref([]);
const columns = ref([]);
const loading = ref(false);
const no_data = ref(false);
const payload = ref(null);
const prevent_watcher = ref(false);
const accessorKey = ref(null);

const height = computed(() => {
  return ((props.data.h || 22) * 20) - 44;
});

const widget_data = computed(() => props.data?.data || {});

async function getReports() {
  loading.value = true;
  activities.value = Object.assign([]);
  columns.value = Object.assign([]);
  payload.value = dashboard_schedule_store.parse_schedule_form_to_server_format(props.data.data);
  accessorKey.value = payload.value?.data?.range.interval;
  try {
    const { data } = await $services.project_management.get_graph({ body: payload.value });
    if (data.data && Object.keys(data.data).length) {
      generateGroupedActivities(data.data);
      no_data.value = false;
    }
    else {
      no_data.value = true;
      columns.value = [];
      activities.value = [];
    }
    loading.value = false;
  }
  catch {
    loading.value = false;
  }
}

function hotSettings() {
  return {
    rowHeaders: true,
    rowHeights: 26,
    viewPortRowRenderingOffset: 100,
    manualColumnResize: false,
    dropdownMenu: false,

  };
}

const colHeaders = function (index) {
  return columns.value[index].text;
};

function fetchDataValues(id, type, item) {
  if (type === 'member') {
    const user = common_store.users.find(a => a.uid === id);
    return user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : null;
  }
  if (type === 'resources') {
    return item.name || id;
  }
  if (type === 'category')
    return common_store.get_category(id)?.name;
  if (type === 'tags') {
    return tags_store.get_tag(id)?.name;
  }
  if (type === 'priority') {
    const priority_values = {
      1: 'Critical',
      2: 'High',
      3: 'Medium',
      4: 'Low',
      5: 'Not set',
    };
    return priority_values[id];
  }

  return null;
}

function mapSeriesName(dataset, meta_data) {
  return dataset.map((series) => {
    const meta_key = intersection(['resources', 'category', 'tags', 'priority'], Object.keys(meta_data))[0];
    let mappedName = series.seriesname;
    if (meta_data && meta_key) {
      const metaList = meta_data[meta_key];
      const metaItem = metaList.find(item => item?.uid === series.seriesname || item === series.seriesname);
      if (metaItem) {
        mappedName = metaItem.external_id ? fetchDataValues(metaItem.external_id, metaItem.type, metaItem) : fetchDataValues(metaItem, meta_key, metaItem);
      }
    }
    return { ...series, seriesname: mappedName };
  });
}

function generateGroupedActivities({ dataset, categories, meta_data }) {
  columns.value = [
    {
      data: accessorKey.value || 'name',
      readOnly: true,
      text: capitalize(accessorKey.value),
      id: accessorKey.value || 'name',
      header: capitalize(accessorKey.value),
    },
  ];

  categories[0].category.forEach((item, key) => {
    const obj = { [accessorKey.value]: item.label, id: item.label };
    const mappedDataset = mapSeriesName(dataset, meta_data);
    mappedDataset.forEach((series) => {
      const seriesname_key = snakeCase(series.seriesname) || 'undefined';
      if (!columns.value.some(col => col.data === seriesname_key)) {
        columns.value.push({
          data: seriesname_key,
          readOnly: true,
          text: series.seriesname || 'undefined',
          id: seriesname_key,
          header: series.seriesname || 'undefined',
        });
      }
      obj[seriesname_key] = convertTableUnitsData(series.data[key]?.value) || 0;
    });
    activities.value.push(obj);
  });
  columns.value = uniqBy(columns.value, 'data');
}

function columnResized(_resized_column, columns_widths) {
  prevent_watcher.value = true;
  dashboard_store.set_table_column_widths(
    props?.id,
    columns_widths,
  );
}
function updatePrintMap() {
  dashboard_store.update_print_map(props.id, {
    type: widget_data.value.type,
    renderAt: `chart-container-${props?.id}`,
    renderType: 'table',
    width: '100%',
    height: '100%',
    dataFormat: 'json',
    chart_name: widget_data.value.name,
    dimensions: {
      x: props.data.x,
      y: props.data.y,
    },
    dataSource: {
      dashboard_index: props.data.i,
      dataset: activities.value,
      columns: columns.value,
      is_transpose: false,
    },
  });
}

watch(() => props.data.data, async (new_val, old_val) => {
  if (new_val && !isEqual(new_val, old_val) && !prevent_watcher.value) {
    await getReports();
    force_update.value++;
    if (props?.id !== 'preview')
      updatePrintMap();
  }
}, { immediate: true, deep: true });
</script>

<template>
  <div>
    <div class="w-full h-full">
      <div v-if="$slots['header-title'] || $slots['header-actions']" class="widget-header group">
        <slot name="header-title" />
        <slot name="header-actions" />
      </div>
      <hawk-loader v-if="loading" />
      <div v-else-if="no_data || !activities?.length" class="text-sm font-semiBold w-full" :class="dashboard_store.is_mobile_view ? 'h-[240px] grid place-items-center' : 'mt-8 flex justify-center'">
        {{ $t('No data present') }}
      </div>
      <a v-else-if="activities && activities?.length && columns?.length">
        <div class="w-full scrollbar" :style="{ height: `${content_height || height}px` }">
          <HawkHandsontable
            :apply-read-only-class="false"
            :data="activities"
            :columns="columns"
            :hot-settings="hotSettings()"
            :columns-menu="['copy']"
            :hot-table-id="id"
            :col-headers="colHeaders"
            :height="id !== 'preview' ? '100%' : '450px'"
            @after-columns-resized="columnResized"
          />
        </div>
      </a>
    </div>
  </div>
</template>
